import { useState } from 'react';
import { Button, Input, FormField } from '../ui';
import { useToast } from '../ui/toast';
import { useCreateVehicle, useUpdateVehicle, type Vehicle, type CreateVehicleData, type UpdateVehicleData } from '../../hooks/useVehicles';

interface VehicleFormData {
  registration: string;
  make: string;
  model: string;
  year: string;
  color: string;
}

interface VehicleFormProps {
  customerId: string;
  initialData?: Partial<VehicleFormData>;
  vehicleId?: string;
  onSuccess: (vehicle: Vehicle) => void;
  onCancel: () => void;
}

export function VehicleForm({
  customerId,
  initialData,
  vehicleId,
  onSuccess,
  onCancel,
}: VehicleFormProps) {
  const { addToast } = useToast();
  const createVehicle = useCreateVehicle();
  const updateVehicle = useUpdateVehicle();

  const [formData, setFormData] = useState<VehicleFormData>({
    registration: initialData?.registration || '',
    make: initialData?.make || '',
    model: initialData?.model || '',
    year: initialData?.year || '',
    color: initialData?.color || '',
  });
  const [errors, setErrors] = useState<Partial<VehicleFormData>>({});

  const isEditing = Boolean(vehicleId);
  const isLoading = createVehicle.isPending || updateVehicle.isPending;

  const validateForm = (): boolean => {
    const newErrors: Partial<VehicleFormData> = {};

    if (!formData.registration.trim()) {
      newErrors.registration = 'Matricula requerida';
    }

    if (!formData.make.trim()) {
      newErrors.make = 'Marca requerida';
    }

    if (!formData.model.trim()) {
      newErrors.model = 'Modelo requerido';
    }

    if (formData.year && (isNaN(Number(formData.year)) || Number(formData.year) < 1900 || Number(formData.year) > new Date().getFullYear() + 1)) {
      newErrors.year = 'Ano invalido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error on change
    if (errors[name as keyof VehicleFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (isEditing) {
        const updateData: UpdateVehicleData = {
          registration: formData.registration,
          make: formData.make,
          model: formData.model,
          year: formData.year ? Number(formData.year) : undefined,
          color: formData.color || undefined,
        };

        const vehicle = await updateVehicle.mutateAsync({ id: vehicleId!, data: updateData });
        addToast({
          variant: 'success',
          title: 'Vehiculo actualizado',
        });
        onSuccess(vehicle);
      } else {
        const createData: CreateVehicleData = {
          customerId,
          registration: formData.registration,
          make: formData.make,
          model: formData.model,
          year: formData.year ? Number(formData.year) : undefined,
          color: formData.color || undefined,
        };

        const vehicle = await createVehicle.mutateAsync(createData);
        addToast({
          variant: 'success',
          title: 'Vehiculo creado',
        });
        onSuccess(vehicle);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al guardar vehiculo';
      addToast({ variant: 'destructive', title: message });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormField
        label="Matricula"
        error={errors.registration}
        required
      >
        <Input
          name="registration"
          value={formData.registration}
          onChange={handleChange}
          placeholder="1234 ABC"
          className="uppercase"
        />
      </FormField>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label="Marca"
          error={errors.make}
          required
        >
          <Input
            name="make"
            value={formData.make}
            onChange={handleChange}
            placeholder="Volkswagen"
          />
        </FormField>

        <FormField
          label="Modelo"
          error={errors.model}
          required
        >
          <Input
            name="model"
            value={formData.model}
            onChange={handleChange}
            placeholder="Golf"
          />
        </FormField>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label="Ano"
          error={errors.year}
        >
          <Input
            type="number"
            name="year"
            value={formData.year}
            onChange={handleChange}
            placeholder="2020"
            min={1900}
            max={new Date().getFullYear() + 1}
          />
        </FormField>

        <FormField
          label="Color"
          error={errors.color}
        >
          <Input
            name="color"
            value={formData.color}
            onChange={handleChange}
            placeholder="Blanco"
          />
        </FormField>
      </div>

      <div className="flex justify-end gap-3 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button type="submit" loading={isLoading}>
          {isEditing ? 'Guardar' : 'Crear vehiculo'}
        </Button>
      </div>
    </form>
  );
}
